import { EmailTemplate } from '../types/crm';
import { getSupabaseAnonKey, getSupabaseUrl } from '../lib/supabase';

const LANGUAGE_NAME_TO_CODE: Record<string, string> = {
  polish: 'pl',
  croatian: 'hr',
  romanian: 'ro',
  lithuanian: 'lt',
  latvian: 'lv',
  russian: 'ru',
  ukrainian: 'uk',
  portuguese: 'pt',
  spanish: 'es',
  french: 'fr',
  german: 'de',
  italian: 'it',
  dutch: 'nl',
  czech: 'cs',
  slovak: 'sk',
  hungarian: 'hu',
  bulgarian: 'bg',
  greek: 'el',
  turkish: 'tr',
  arabic: 'ar',
  chinese: 'zh',
  'chinese (simplified)': 'zh',
  japanese: 'ja',
  english: 'en',
};

type TranslationResult = { subject: string; body: string };

function getAnonHeaders(): HeadersInit {
  const anonKey = getSupabaseAnonKey();
  return {
    Authorization: `Bearer ${anonKey}`,
    apikey: anonKey,
    'Content-Type': 'application/json',
  };
}

function protectPlaceholders(input: string): { protectedText: string; map: Map<string, string> } {
  const placeholderRegex = /\{\{[^}]+\}\}/g;
  const map = new Map<string, string>();
  let idx = 0;
  const protectedText = input.replace(placeholderRegex, (match) => {
    const token = `__PH_${idx}__`;
    map.set(token, match);
    idx += 1;
    return token;
  });
  return { protectedText, map };
}

function restorePlaceholders(input: string, map: Map<string, string>): string {
  let out = input;
  map.forEach((placeholder, token) => {
    out = out.replaceAll(token, placeholder);
  });
  return out;
}

export function templateBaseName(name: string): string {
  return name.replace(/\s+\([^()]+\)\s*$/, '').trim();
}

export function findPreferredVariantTemplate(
  templates: EmailTemplate[],
  selectedTemplate: EmailTemplate,
  preferredLanguage?: string
): EmailTemplate | undefined {
  if (!preferredLanguage?.trim()) return undefined;
  const lang = preferredLanguage.trim().toLowerCase();
  const base = templateBaseName(selectedTemplate.name).toLowerCase();
  return templates.find((t) => {
    const tBase = templateBaseName(t.name).toLowerCase();
    if (tBase !== base) return false;
    const suffixMatch = t.name.match(/\(([^()]+)\)\s*$/);
    return !!suffixMatch && suffixMatch[1].trim().toLowerCase() === lang;
  });
}

export type ResolvedTemplateResult = {
  template: EmailTemplate;
  languageUsed: string;
  translated: boolean;
};

export async function resolveTemplateForLanguage(
  templates: EmailTemplate[],
  baseTemplate: EmailTemplate,
  preferredLanguage: string | undefined,
  translateCache: Map<string, TranslationResult>
): Promise<ResolvedTemplateResult> {
  const normalizedLang = preferredLanguage?.trim();
  if (!normalizedLang) {
    return { template: baseTemplate, languageUsed: 'default', translated: false };
  }

  const preferredVariant = findPreferredVariantTemplate(templates, baseTemplate, normalizedLang);
  if (preferredVariant) {
    return { template: preferredVariant, languageUsed: normalizedLang, translated: false };
  }

  const langCode = LANGUAGE_NAME_TO_CODE[normalizedLang.toLowerCase()];
  if (!langCode || langCode === 'en') {
    return { template: baseTemplate, languageUsed: normalizedLang, translated: false };
  }

  const cacheKey = `${baseTemplate.id}:${langCode}`;
  const cached = translateCache.get(cacheKey);
  if (cached) {
    return {
      template: {
        ...baseTemplate,
        subject: cached.subject,
        body: cached.body,
      },
      languageUsed: normalizedLang,
      translated: true,
    };
  }

  const subjectProtected = protectPlaceholders(baseTemplate.subject || '');
  const bodyProtected = protectPlaceholders(baseTemplate.body || '');
  const endpoint = `${getSupabaseUrl().replace(/\/$/, '')}/functions/v1/translate-email`;
  const response = await fetch(endpoint, {
    method: 'POST',
    headers: getAnonHeaders(),
    body: JSON.stringify({
      html: bodyProtected.protectedText,
      subject: subjectProtected.protectedText,
      languages: [langCode],
    }),
  });
  const data = await response.json().catch(() => null);
  if (!response.ok || !data || typeof data !== 'object') {
    return { template: baseTemplate, languageUsed: normalizedLang, translated: false };
  }

  const translations = (data as { translations?: Array<{ subject?: string; body?: string }> }).translations || [];
  const first = translations[0];
  if (!first) {
    return { template: baseTemplate, languageUsed: normalizedLang, translated: false };
  }

  const translated: TranslationResult = {
    subject: restorePlaceholders(first.subject || baseTemplate.subject, subjectProtected.map),
    body: restorePlaceholders(first.body || baseTemplate.body, bodyProtected.map),
  };
  translateCache.set(cacheKey, translated);
  return {
    template: {
      ...baseTemplate,
      subject: translated.subject,
      body: translated.body,
    },
    languageUsed: normalizedLang,
    translated: true,
  };
}
